import React, { useEffect, useState } from "react";
import type { Editor } from "@tiptap/react";
import {
  ArrowDown,
  ArrowUp,
  BookOpen,
  ChevronDown,
  FileSignature,
  IndianRupee,
  Plus,
  ScrollText,
  StickyNote,
  Table2,
  Trash2,
  Type,
  Wallet,
} from "lucide-react";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { ProposalSection } from "@/types/proposal";
import { useConfirm } from "@/context/ConfirmContext";

export interface EditableSection extends ProposalSection {
  /** Stable key for React (the saved id, or a temporary one for a new section) */
  key: string;
}

const SECTION_TYPES: Record<string, { label: string; icon: React.ElementType; hint: string; single?: boolean }> = {
  cover: { label: "Cover page", icon: BookOpen, hint: "Built from the proposal, customer and your company details", single: true },
  rich_text: { label: "Text", icon: Type, hint: "Any content: summary, needs, scope, timeline, about us" },
  estimate: { label: "Price estimate", icon: IndianRupee, hint: "The indicative price table, with optional text above it", single: true },
  terms: { label: "Terms", icon: ScrollText, hint: "Validity, assumptions and next steps" },
  acceptance: { label: "Acceptance", icon: FileSignature, hint: "The statement the customer accepts, with a signature block", single: true },
  note: { label: "Note box", icon: StickyNote, hint: "A highlighted box, for the scope note or anything the customer must not miss" },
  payment: { label: "Payment schedule", icon: Wallet, hint: "Milestones as percentages of the total, with the rupee amounts worked out", single: true },
  facts: { label: "Details table", icon: Table2, hint: "Label / value rows, such as the parties or a statement of work (agreements)" },
};

/** Advice for whoever fills the section in (from the agency's proposal template) */
const TIPS: Record<string, string> = {
  note: "Keep it honest: if the budget or scale wasn't given, say the price is an assumption, and name anything the client must confirm.",
  estimate: "Decide the final total first. Subtotal = total ÷ 1.18 and GST = subtotal × 0.18 (59,000 = 50,000 + 9,000); tick \"amounts include GST\" to type the final prices.",
  payment: "Typical splits: 50 / 50 for small sites, 40 / 30 / 30 for larger builds and apps. The rows must add up to 100%.",
  terms: "Typical: 2 rounds of revisions, 3 months of free bug-fix support after launch.",
};
const titleTip = (title: string) =>
  /not included|running costs/i.test(title) ? "Always list what isn't included and the running costs the client pays to providers; it prevents disputes later." : null;

const DEFAULT_TITLES: Record<string, string> = {
  cover: "Cover",
  rich_text: "New section",
  estimate: "Investment (indicative)",
  terms: "Terms and next steps",
  acceptance: "Acceptance",
  note: "Scope note",
  payment: "Payment terms",
  facts: "Details",
};

let seq = 0;
export const newSectionKey = () => `new-${Date.now().toString(36)}-${(seq++).toString(36)}`;

export const toEditableSections = (sections: ProposalSection[] = []): EditableSection[] =>
  sections.map((s) => ({ ...s, key: s.id || newSectionKey(), html: s.html ?? "" }));

/** Sections as the API expects them */
export const toApiSections = (sections: EditableSection[]): ProposalSection[] =>
  sections.map(({ key, ...s }) => ({
    id: s.id || key,
    type: s.type,
    title: s.title.trim(),
    html: s.type === "cover" ? null : s.html || "",
    ...(s.type === "cover" ? { variant: s.variant === "compact" ? ("compact" as const) : ("full" as const) } : {}),
    ...(s.type === "payment" ? { rows: (s.rows || []).map((r) => ({ label: r.label.trim(), percent: Number(r.percent) || 0 })) } : {}),
    ...(s.type === "facts"
      ? {
          facts: (s.facts || []).map((f) => ({ label: f.label.trim(), value: f.value, ...(f.group?.trim() ? { group: f.group.trim() } : {}) })),
          ...(s.variant === "columns" ? { variant: "columns" as const } : {}),
        }
      : {}),
    ...(s.numbered === false ? { numbered: false } : {}),
    ...(s.show_title === false ? { show_title: false } : {}),
  }));

/** Problems that would stop a save, or null */
export function sectionProblem(sections: EditableSection[]): string | null {
  if (!sections.length) return "Add at least one section";
  for (const [i, s] of sections.entries()) {
    if (!s.title.trim()) return `Section ${i + 1} needs a title`;
    if (s.type === "facts" && (s.facts || []).some((f) => !f.label.trim())) return `"${s.title}": every row needs a label`;
    if (s.type === "payment") {
      const rows = s.rows || [];
      if (rows.some((r) => !r.label.trim())) return `"${s.title}": every payment row needs a name`;
      if (rows.length && Math.abs(rows.reduce((t, r) => t + (Number(r.percent) || 0), 0) - 100) > 0.001) return `"${s.title}": the payment rows must add up to 100%`;
    }
  }
  return null;
}

/**
 * Ordered list of proposal sections: rename, reorder, add, remove and edit the text of each.
 * `renderExtra` adds content under a section (the estimate table under the price section).
 */
export function SectionsEditor({
  sections,
  onChange,
  disabled,
  activeEditor,
  renderExtra,
  openKey,
  allowFacts,
}: {
  sections: EditableSection[];
  onChange: (next: EditableSection[]) => void;
  disabled?: boolean;
  activeEditor?: React.MutableRefObject<Editor | null>;
  renderExtra?: (section: EditableSection) => React.ReactNode;
  /** When it changes, that section opens and the others fold away (the outline beside the editor sets it) */
  openKey?: string | null;
  /** Offer the details table (agreements only; a proposal's document doesn't draw one) */
  allowFacts?: boolean;
}) {
  const confirm = useConfirm();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (openKey) setCollapsed(Object.fromEntries(sections.map((s) => [s.key, s.key !== openKey])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey]);

  const update = (key: string, patch: Partial<EditableSection>) =>
    onChange(sections.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  const move = (idx: number, dir: -1 | 1) => {
    const next = [...sections];
    const [s] = next.splice(idx, 1);
    if (!s) return;
    next.splice(idx + dir, 0, s);
    onChange(next);
  };
  const remove = async (key: string) => {
    const s = sections.find((x) => x.key === key);
    const hasText = !!s?.html && s.html.replace(/<[^>]*>/g, "").trim().length > 0;
    if (hasText && !(await confirm({ title: `Remove the section "${s?.title || "Untitled"}"?`, message: "Its text will be removed too.", confirmLabel: "Remove", destructive: true }))) return;
    onChange(sections.filter((x) => x.key !== key));
  };
  const add = (type: string) => {
    const s: EditableSection = {
      key: newSectionKey(),
      type,
      title: DEFAULT_TITLES[type] || "New section",
      html: type === "cover" ? null : "",
      ...(type === "cover" ? { variant: "full" as const } : {}),
      ...(type === "payment" ? { rows: [{ label: "Advance", percent: 50 }, { label: "On launch", percent: 50 }] } : {}),
      ...(type === "facts" ? { facts: [{ label: "", value: "" }] } : {}),
    };
    const next = [...sections];
    if (type === "cover") next.unshift(s);
    else {
      // Keep the acceptance section last
      const acc = next.findIndex((x) => x.type === "acceptance");
      next.splice(type !== "acceptance" && acc >= 0 ? acc : next.length, 0, s);
    }
    onChange(next);
  };
  const allCollapsed = sections.length > 0 && sections.every((s) => collapsed[s.key]);
  const toggleAll = () => setCollapsed(allCollapsed ? {} : Object.fromEntries(sections.map((s) => [s.key, true])));

  const addMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" disabled={disabled}>
          <Plus className="size-3.5" /> Add section
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {Object.entries(SECTION_TYPES).filter(([type]) => type !== "facts" || allowFacts).map(([type, meta]) => {
          const taken = !!meta.single && sections.some((s) => s.type === type);
          return (
            <DropdownMenuItem key={type} disabled={taken} onSelect={() => add(type)} className="items-start gap-2.5 py-2">
              <meta.icon className="size-4 mt-0.5 text-primary shrink-0" />
              <div>
                <div className="text-xs font-semibold">
                  {meta.label}
                  {taken && <span className="font-normal text-muted-foreground"> · already added</span>}
                </div>
                <div className="text-[11px] text-muted-foreground">{meta.hint}</div>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  let number = 0;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold">
          Sections <span className="text-muted-foreground font-normal">({sections.length})</span>
        </h3>
        <div className="flex items-center gap-2">
          {sections.length > 1 && (
            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={toggleAll}>
              {allCollapsed ? "Expand all" : "Collapse all"}
            </Button>
          )}
          {addMenu}
        </div>
      </div>

      {sections.length === 0 && (
        <div className="rounded-2xl border border-dashed p-8 text-center text-sm text-muted-foreground">
          No sections yet. Add one to start writing.
        </div>
      )}

      {sections.map((s, idx) => {
        const meta = SECTION_TYPES[s.type] ?? SECTION_TYPES.rich_text!;
        const isCover = s.type === "cover";
        // The cover and note boxes aren't numbered, like in the PDF; an agreement section can also opt out
        const numbered = !isCover && s.type !== "note" && s.numbered !== false;
        if (numbered) number++;
        const open = !collapsed[s.key];
        return (
          <div key={s.key} id={`section-${s.key}`} className="bg-card border border-border rounded-lg overflow-hidden scroll-mt-4">
            <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/20">
              <button
                type="button"
                onClick={() => setCollapsed((c) => ({ ...c, [s.key]: open }))}
                className="size-7 inline-flex items-center justify-center rounded hover:bg-muted text-muted-foreground shrink-0"
                aria-label={open ? "Collapse section" : "Expand section"}
              >
                <ChevronDown className={cn("size-4 transition-transform", !open && "-rotate-90")} />
              </button>
              <span className="text-xs font-bold text-muted-foreground w-5 text-right shrink-0">{numbered ? `${number}.` : ""}</span>
              <input
                value={s.title}
                disabled={disabled}
                onChange={(e) => update(s.key, { title: e.target.value })}
                placeholder="Section title"
                maxLength={200}
                className="flex-1 min-w-0 h-8 bg-transparent text-sm font-semibold focus:outline-none focus:bg-background rounded px-2 disabled:opacity-100"
              />
              <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted px-2 py-0.5 rounded-full shrink-0">
                <meta.icon className="size-3" /> {meta.label}
              </span>
              {!disabled && (
                <div className="flex items-center shrink-0">
                  <button type="button" title="Move up" disabled={idx === 0} onClick={() => move(idx, -1)} className="size-7 inline-flex items-center justify-center rounded hover:bg-muted text-muted-foreground disabled:opacity-30">
                    <ArrowUp className="size-3.5" />
                  </button>
                  <button type="button" title="Move down" disabled={idx === sections.length - 1} onClick={() => move(idx, 1)} className="size-7 inline-flex items-center justify-center rounded hover:bg-muted text-muted-foreground disabled:opacity-30">
                    <ArrowDown className="size-3.5" />
                  </button>
                  <button type="button" title="Remove section" onClick={() => remove(s.key)} className="size-7 inline-flex items-center justify-center rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive">
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              )}
            </div>
            {open && (
              <div className="p-3 space-y-3">
                {isCover ? (
                  <div className="space-y-2">
                    <div role="radiogroup" aria-label="Cover style" className="grid gap-2 sm:grid-cols-2">
                      {([["full", "Title page", "A whole first page with the company details, who it is prepared for and when"], ["compact", "Compact heading", "A title, \"Client: Project\" and who prepared it at the top of the first page"]] as const).map(([v, label, note]) => {
                        const on = (s.variant || "full") === v;
                        return (
                          <label key={v} className={cn("flex cursor-pointer flex-col gap-0.5 rounded-lg border px-3 py-2", on ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50", disabled && "cursor-not-allowed opacity-70")}>
                            <span className="flex items-center gap-2 text-xs font-semibold">
                              <input type="radio" className="accent-primary" name={`cover-${s.key}`} checked={on} disabled={disabled} onChange={() => update(s.key, { variant: v })} />
                              {label}
                            </span>
                            <span className="pl-6 text-[11px] text-muted-foreground">{note}</span>
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground">Remove this section for a proposal with no cover at all.</p>
                  </div>
                ) : s.type === "payment" ? (
                  <PaymentRows rows={s.rows || []} onChange={(rows) => update(s.key, { rows })} disabled={disabled} intro={
                    <RichTextEditor value={s.html || ""} onChange={(html) => update(s.key, { html })} disabled={disabled} minHeight={50} placeholder="Optional text above the payment schedule" onFocusEditor={(ed) => activeEditor && (activeEditor.current = ed)} />
                  } />
                ) : s.type === "facts" ? (
                  <FactsRows
                    facts={s.facts || []}
                    columns={s.variant === "columns"}
                    onChange={(facts, columns) => update(s.key, { facts, variant: columns ? "columns" : undefined })}
                    disabled={disabled}
                  />
                ) : (
                  <RichTextEditor
                    value={s.html || ""}
                    onChange={(html) => update(s.key, { html })}
                    disabled={disabled}
                    minHeight={s.type === "estimate" || s.type === "acceptance" ? 60 : 110}
                    placeholder={s.type === "estimate" ? "Optional text above the price table" : s.type === "note" ? "What this proposal covers, any assumption made, and what the client must confirm…" : "Write this section…"}
                    onFocusEditor={(ed) => activeEditor && (activeEditor.current = ed)}
                  />
                )}
                {!isCover && s.type !== "note" && (
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11.5px] text-muted-foreground">
                    <label className="inline-flex items-center gap-1.5">
                      <input type="checkbox" className="size-3.5 accent-primary" checked={s.numbered !== false} disabled={disabled} onChange={(e) => update(s.key, { numbered: e.target.checked ? undefined : false })} />
                      Number this section
                    </label>
                    <label className="inline-flex items-center gap-1.5">
                      <input type="checkbox" className="size-3.5 accent-primary" checked={s.show_title !== false} disabled={disabled} onChange={(e) => update(s.key, { show_title: e.target.checked ? undefined : false })} />
                      Show its title in the document
                    </label>
                  </div>
                )}
                {!disabled && (TIPS[s.type] || titleTip(s.title)) && <p className="text-[11.5px] leading-snug text-muted-foreground">Tip: {TIPS[s.type] || titleTip(s.title)}</p>}
                {renderExtra?.(s)}
              </div>
            )}
          </div>
        );
      })}

      {sections.length > 3 && !disabled && <div className="flex justify-end">{addMenu}</div>}
    </div>
  );
}

/**
 * Label / value rows (agreements): the parties box and the statement of work. Values may use {{merge.fields}} and line breaks.
 * "Side by side" gives each group its own column under a heading (the service provider and the client).
 */
function FactsRows({ facts, columns, onChange, disabled }: { facts: Array<{ label: string; value: string; group?: string }>; columns: boolean; onChange: (facts: Array<{ label: string; value: string; group?: string }>, columns: boolean) => void; disabled?: boolean }) {
  const set = (i: number, patch: Partial<{ label: string; value: string; group: string }>) => onChange(facts.map((f, j) => (j === i ? { ...f, ...patch } : f)), columns);
  return (
    <div className="space-y-2">
      <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <input type="checkbox" className="size-3.5 accent-primary" checked={columns} disabled={disabled} onChange={(e) => onChange(facts, e.target.checked)} />
        Show the groups side by side (like the parties box)
      </label>
      <div className="space-y-1.5">
        {facts.map((f, i) => (
          <div key={i} className="flex flex-wrap items-start gap-1.5">
            <div className="w-44 shrink-0 space-y-1">
              <input value={f.label} disabled={disabled} maxLength={120} placeholder="Label" aria-label={`Row ${i + 1} label`} onChange={(e) => set(i, { label: e.target.value })} className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs font-medium" />
              {columns && <input value={f.group || ""} disabled={disabled} maxLength={80} placeholder="Column heading" aria-label={`Row ${i + 1} column`} onChange={(e) => set(i, { group: e.target.value })} className="h-7 w-full rounded-md border border-input bg-background px-2 text-[11px] text-muted-foreground" />}
            </div>
            <textarea value={f.value} disabled={disabled} maxLength={4000} rows={Math.min(6, Math.max(1, f.value.split("\n").length))} placeholder="Value" aria-label={`Row ${i + 1} value`} onChange={(e) => set(i, { value: e.target.value })} className="min-w-[200px] flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
            {!disabled && (
              <button type="button" title="Remove row" onClick={() => onChange(facts.filter((_, j) => j !== i), columns)} className="size-8 inline-flex items-center justify-center rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive">
                <Trash2 className="size-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      {!disabled && (
        <Button type="button" size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => onChange([...facts, { label: "", value: "", ...(columns && facts.length ? { group: facts[facts.length - 1]!.group } : {}) }], columns)}>
          <Plus className="size-3" /> Add row
        </Button>
      )}
    </div>
  );
}

/** The milestones of a payment schedule: a name and a percent each, with presets, and a check that they add up to 100% */
function PaymentRows({ rows, onChange, disabled, intro }: { rows: Array<{ label: string; percent: number }>; onChange: (rows: Array<{ label: string; percent: number }>) => void; disabled?: boolean; intro?: React.ReactNode }) {
  const total = Math.round(rows.reduce((t, r) => t + (Number(r.percent) || 0), 0) * 100) / 100;
  const set = (i: number, patch: Partial<{ label: string; percent: number }>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const preset = (parts: Array<[string, number]>) => onChange(parts.map(([label, percent]) => ({ label, percent })));
  return (
    <div className="space-y-2.5">
      {intro}
      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              value={r.percent}
              type="number"
              min={0}
              max={100}
              step="any"
              disabled={disabled}
              aria-label={`Milestone ${i + 1} percent`}
              onChange={(e) => set(i, { percent: Number(e.target.value) })}
              className="h-8 w-20 rounded-md border border-input bg-background px-2 text-right text-xs"
            />
            <span className="text-xs text-muted-foreground">%</span>
            <input
              value={r.label}
              disabled={disabled}
              maxLength={120}
              aria-label={`Milestone ${i + 1} name`}
              onChange={(e) => set(i, { label: e.target.value })}
              placeholder="e.g. Advance, On launch"
              className="h-8 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-xs"
            />
            {!disabled && (
              <button type="button" title="Remove milestone" onClick={() => onChange(rows.filter((_, j) => j !== i))} className="size-8 inline-flex items-center justify-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                <Trash2 className="size-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {!disabled && (
          <>
            <Button type="button" size="sm" variant="outline" className="h-7 gap-1 text-xs" disabled={rows.length >= 12} onClick={() => onChange([...rows, { label: "", percent: 0 }])}>
              <Plus className="size-3.5" /> Add milestone
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => preset([["Advance", 50], ["On launch", 50]])}>50 / 50</Button>
            <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => preset([["Advance", 40], ["On the first milestone", 30], ["On launch", 30]])}>40 / 30 / 30</Button>
          </>
        )}
        <span className={cn("ml-auto text-xs font-semibold", Math.abs(total - 100) < 0.001 ? "text-success" : "text-destructive")}>{total}% of 100%</span>
      </div>
    </div>
  );
}
